import { useState, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import LogoIcon from '../components/LogoIcon';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = (location.state as { from?: string })?.from || '/dashboard';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!email || !password) return;
        setLoading(true);
        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })
                ?.response?.data?.error || '登录失败，请检查邮箱和密码';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4">
            <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <div className="mb-4 shadow-lg shadow-brand-900/50 rounded-2xl">
                        <LogoIcon size={48} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">税码通</h1>
                    <p className="text-slate-500 text-sm mt-1">AI 智能 HTS 编码分类平台</p>
                </div>

                <div className="card">
                    <h2 className="text-lg font-semibold text-white mb-6">登录账户</h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                邮箱地址
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                autoFocus
                                className="input-field"
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs font-medium text-slate-400">密码</label>
                                <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-brand-400 transition-colors">
                                    忘记密码？
                                </Link>
                            </div>
                            <div className="relative">
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    className="input-field pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                >
                                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !email || !password}
                            className="btn-primary w-full mt-2"
                        >
                            {loading ? (
                                <><Loader2 size={16} className="animate-spin" /> 登录中...</>
                            ) : (
                                '登录'
                            )}
                        </button>
                    </form>

                    <p className="text-center text-sm text-slate-500 mt-5">
                        还没有账户？{' '}
                        <Link to="/register" className="text-brand-400 hover:text-brand-300 font-medium">
                            免费注册
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
